/*
 * Why plans ended (Insights, owner 2026-11-04): every repeating plan that ended or paused, how long it lasted, who
 * cleaned it, the share of plans still going 3 and 6 months after they started, and money per cleaner hour where the
 * visit timer ran. Read only; owners and managers (insights.read).
 */
import { z } from "zod";

export const PLAN_STATES = ["running", "ended", "paused"] as const;
export type PlanState = (typeof PLAN_STATES)[number];

export const planEndRow = z.object({
  clientId: z.string(),
  clientName: z.string(),
  /** ended: the client was stopped or the last job's end day passed. paused: the jobs are off, the client is not stopped. */
  state: z.enum(PLAN_STATES),
  /** The first done visit, else the job's start day. */
  start: z.string(),
  /** The last done visit, else the job's end or start day. Null while it runs. */
  end: z.string().nullable(),
  /** How often, from the plan's latest job (weekly, biweekly, monthly...). */
  frequency: z.string(),
  visits: z.number(),
  /** Months from the start to one round after the last visit (to today while it runs), one decimal. */
  months: z.number(),
  /** Why the client stopped, when one was saved (INACTIVE_REASONS), with its note. */
  reason: z.string().nullable(),
  reasonNote: z.string().nullable(),
  firstCleaner: z.string().nullable(),
  /** Different people on the first 3 visits that had someone on them. */
  cleanersFirst3: z.number(),
  cleanersAll: z.number(),
  /** Times the people changed from one visit to the next. */
  crewChanges: z.number(),
  /** Money per cleaner hour on visits with timer time, before tips. Null when no visit has time. */
  perHourCents: z.number().nullable(),
});
export type PlanEndRow = z.infer<typeof planEndRow>;

export const planCohort = z.object({
  /** YYYY-MM the plans started in. */
  month: z.string(),
  started: z.number(),
  /** Plans old enough to judge at 3 months, and how many were still going then. */
  due3: z.number(),
  kept3: z.number(),
  due6: z.number(),
  kept6: z.number(),
});
export type PlanCohort = z.infer<typeof planCohort>;

export const planGroupRow = z.object({
  name: z.string(),
  plans: z.number(),
  running: z.number(),
  /** Ended or paused. */
  stopped: z.number(),
  /** Average done visits of the stopped plans. Null when none stopped. */
  averageVisits: z.number().nullable(),
  due3: z.number(),
  kept3: z.number(),
  due6: z.number(),
  kept6: z.number(),
  perHourCents: z.number().nullable(),
  timedVisits: z.number(),
});
export type PlanGroupRow = z.infer<typeof planGroupRow>;

export const planEndsAnswer = z.object({
  today: z.string(),
  /** Every plan that has started, with the ones still going. */
  total: planGroupRow,
  /** Plans that started in the last 12 months: the new plans the goal is about. */
  recent: planGroupRow,
  ended: z.array(planEndRow),
  cohorts: z.array(planCohort),
  byFrequency: z.array(planGroupRow),
  byFirstCleaner: z.array(planGroupRow),
  /** Plans with a first visit still to come (not counted above). */
  upcoming: z.number(),
});
export type PlanEndsAnswer = z.infer<typeof planEndsAnswer>;
